import React, { useContext, useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import AuthService from "services/auth.service";
import {
  initiateMediaUpload,
  updateMediaUploadProgress,
  uploadMedia,
} from "actions/media";
import shortid from "shortid";
import { actionGroup, arrangeWidget, createWidget, deleteWidgets, updateWidgetContent } from "actions/editor";
import S3Upload from "react-s3-uploader/s3upload";
import StoryboardContext from "components/Storyboard/context";
import { IMAGE } from "constants/widgetTypes";
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";
import { EditorContext } from "components/Editor";
import { fetchImageAsFile } from "components/Editor/helper/image";

import cx from "classnames";
import _ from "lodash";

import WidgetActions from "components/Editor/components/properties/widgetActions";
import { MediaLibraryModal } from "components/MediaLibrary";

import styles from "./aiImage.module.scss";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const getImageDimensions = (file) =>
  new Promise((resolve) => {
    if (!(file instanceof File) || !file.type.startsWith("image/")) {
      resolve(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const dimension = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dimension.width && dimension.height ? dimension : null);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    img.src = url;
  });

const AiImageWidgetComponent = function ({
  widgetProps,
  widgetRef,
  className,
  style,
  children,
}) {
  const dispatch = useDispatch();
  const store = useStore();
  const ref = widgetRef;
  const DEFAULT_FALLBACK_URL = 'https://ik.imagekit.io/zvzrxdvzjue/tr:w-1086,h-616,c-force:q-80,f-webp/5ae18247c9e77c0001c91375/images/6995719c89f8f03e5c8efc5c/8912b7a4-e833-47e7-8313-e868e778e1a4?ik-sdk-version=react-1.1.1';

  const editorContext = useContext(EditorContext);
  const storyboardContext = useContext(StoryboardContext);
  const [uploadTriggered, setUploadTriggered] = useState(false);

  const editorSession = editorContext.editorSession.id;
  const desktopLayout = _.get(widgetProps, "properties.desktop.layout");
  const mobileLayout = _.get(widgetProps, "properties.mobile.layout");
  const desktopTransform = _.get(desktopLayout, "transform.translate");
  const mobileTransform = _.get(mobileLayout, "transform.translate");
  const desktopWidth = _.get(desktopLayout, "width");
  const desktopHeight = _.get(desktopLayout, "height");
  const mobileWidth = _.get(mobileLayout, "width");
  const mobileHeight = _.get(mobileLayout, "height");
  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");
  const parent = _.get(widgetProps, "parent");
  const order = _.get(widgetProps, "order");
  const desktopBorder = _.get(widgetProps, "properties.desktop.border");
  const mobileBorder = _.get(widgetProps, "properties.mobile.border");
  const desktopShadow = _.get(widgetProps, "properties.desktop.shadow");
  const mobileShadow = _.get(widgetProps, "properties.mobile.shadow");
  const desktopAlt = _.get(widgetProps, "properties.desktop.alt");
  const mobileAlt = _.get(widgetProps, "properties.mobile.alt");

  const imageUrl = _.get(widgetProps, "content.image_url");
  const fetchFailed = _.get(widgetProps, "content.fetchFailed", false);
  const loadingWidgetId = `widget_loading_${id}`;

  const loadingWidget = {
    type: "cleverstory.v2.widgets.image",
    name: "Image",
    properties: {
      desktop: {
        visible: true,
        layout: {
          transform: { translate: desktopTransform, rotate: "0deg" },
          width: desktopWidth,
          height: desktopHeight,
        },
        crop: null,
        alt: null,
        animation: null,
        border: null,
        position: null,
        shadow: null,
      },
      mobile: {
        visible: true,
        layout: {
          transform: { translate: mobileTransform, rotate: "0deg" },
          width: mobileWidth,
          height: mobileHeight,
        },
        crop: null,
        alt: null,
        animation: null,
        border: null,
        position: null,
        shadow: null,
      },
    },
    content: null,
    uid: loadingWidgetId,
    parent,
  };

  const removeSpecialCharactersFromName = (name) => {
    return name.replace(/\u202F/g, "");
  };


  useEffect(() => {
    if (!imageUrl || uploadTriggered) return;

    setUploadTriggered(true);
    triggerUpload(imageUrl);
  }, [imageUrl]);

  const triggerUpload = useCallback(
    (file) => {
      if (!(file instanceof File)) {
        console.warn("Not a valid file:", file);
        return;
      }

      try {
        processAndUpload(file);
      } catch (err) {
        console.warn("Primary image fetch failed", err);
        const currentContent = _.get(widgetProps, "content", {}) || {};
        dispatch(updateWidgetContent(editorSession, id, {...currentContent, fetchFailed: true,}));
      }
    },
    [id, parent, editorSession, widgetProps]
  );

  const processAndUpload = (file) => {
    dispatch(createWidget(editorSession, [loadingWidget], parent, true, true));

    const processedFile = {
      id,
      name: file.name,
      type: file.type,
      size: file.size / (1024 * 1024),
      progress: 0,
      mediaPaste: true,
      file,
    };

    const updatedName = removeSpecialCharactersFromName(processedFile.name);

    const s3Uploader = new S3Upload({
      signingUrl: "/file_upload/initiate",
      signingUrlMethod: "POST",
      signingUrlHeaders: {
        Authorization: `Bearer ${AuthService.getAccessToken()}`,
      },
      server: API_URL,
      scrubFilename: (filename) => {
        let name = removeSpecialCharactersFromName(filename);
        return name.replace(/[^\w\d_\-.\s]+/gi, "");
      },
      signingUrlQueryParams: { originalName: encodeURIComponent(updatedName) },
      contentDisposition: "auto",
      preprocess: (file, next) => next(file),
      uploadRequestHeaders: {},
      onFinishS3Put: (info, uploadedFile) =>
        handleUploadComplete(info, uploadedFile, loadingWidgetId),
      onProgress: (progress) =>
        dispatch(updateMediaUploadProgress(id, progress)),
      onError: () => {
        toast(
          <FailureToast
            heading="Upload Failed"
            description="Unable to upload the file, Please try again."
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
            toastId: `upload-error-${id}-toast`,
          }
        );
        setTimeout(
          () => dispatch(deleteWidgets(editorSession, [loadingWidgetId, id])),
          30
        );
      },
      files: [processedFile.file],
    });

    handleUploadStart(s3Uploader, processedFile);
  };

  const handleUploadComplete = async (info, uploadedFile, loadingWidgetId) => {
    const formats = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/svg",
      "image/bmp",
      "image/gif",
      "image/svg+xml",
    ];
    if (info && formats.includes(uploadedFile.type)) {
      const dimension = await getImageDimensions(imageUrl);

      handleUploadMedia(
        {
          fileName: uploadedFile.name,
          contentType: uploadedFile.type,
          signedUrl: info.signedUrl,
          width: dimension?.width || parseInt(desktopWidth, 10),
          height: dimension?.height || parseInt(desktopHeight, 10),
          storyboard: _.get(storyboardContext, "id"),
        },
        uploadedFile,
        loadingWidgetId
      );
    } else {
      setTimeout(
        () => dispatch(deleteWidgets(editorSession, [loadingWidgetId, id])),
        30
      );
    }
  };

  const handleUploadMedia = (request, uploadedFile, loadingWidgetId) => {
    dispatch(
      uploadMedia(
        id,
        request,
        (response) => {
          if (response) handleCreateImage([response]);
          setTimeout(
            () => dispatch(deleteWidgets(editorSession, [loadingWidgetId, id])),
            30
          );
        },
        true
      )
    );
  };

  const handleUploadStart = (instance, processedFile) => {
    dispatch(
      initiateMediaUpload(
        {
          id: processedFile.id,
          name: processedFile.name,
          type: processedFile.type,
          size: processedFile.size,
          progress: 0,
          instance,
        },
        false,
        true
      )
    );
  };

  const handleCreateImage = (medias) => {
    actionGroup.start();
    const imageMedias = medias.filter((media) => media.type === "image");
    const siblingCount = (store.getState()[editorSession]?.present?.widgets || []).filter(
      (w) => w.parent === parent
    ).length;

    imageMedias.forEach((media, index) => {
      if (media.type !== "image") return;
      const newUid = `widget_${shortid.generate()}`;

      dispatch(
        createWidget(
          editorSession,
          [
            {
              uid: newUid,
              type: IMAGE,
              name: "Image",
              order: order || 0,
              properties: {
                desktop: {
                  visible: true,
                  layout: {
                    transform: { translate: desktopTransform, rotate: "0deg" },
                    width: desktopWidth,
                    height: desktopHeight,
                  },
                  crop: null,
                  alt: desktopAlt || null,
                  animation: null,
                  border: desktopBorder || null,
                  position: null,
                  shadow: desktopShadow || null,
                },
                mobile: {
                  visible: true,
                  layout: {
                    transform: { translate: mobileTransform, rotate: "0deg" },
                    width: mobileWidth,
                    height: mobileHeight,
                  },
                  crop: null,
                  alt: mobileAlt || desktopAlt || null,
                  animation: null,
                  border: mobileBorder || desktopBorder || null,
                  position: null,
                  shadow: mobileShadow || desktopShadow || null,
                },
              },
              content: media,
            },
          ],
          parent,
          false
        )
      );
      dispatch(arrangeWidget(editorSession, { uid: newUid, parent }, siblingCount + index));
    });
    actionGroup.end();
  };

  return (
    <div
      id={id}
      ref={ref}
      className={cx(styles.aiImageWidgetContainer, className)}
      style={style}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      {/* Show placeholder while waiting for imageUrl, uploading, or when fetch failed */}
      {(!uploadTriggered || fetchFailed) && (
        <div className={styles.imageWrapper} data-fetch-failed={fetchFailed}>
          <img
            src={DEFAULT_FALLBACK_URL}
            alt="temp"
            className={styles.image}
          />
        </div>
      )}
    </div>
  );
};

const AiImageFetchFailedDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = useContext(EditorContext);
  const storyboardContext = useContext(StoryboardContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "uid");
  const parent = _.get(props, "parent");
  const desktopLayout = _.get(props, "properties.desktop.layout");
  const mobileLayout = _.get(props, "properties.mobile.layout");
  const desktopTransform = _.get(desktopLayout, "transform.translate");
  const mobileTransform = _.get(mobileLayout, "transform.translate");
  const desktopWidth = _.get(desktopLayout, "width");
  const desktopHeight = _.get(desktopLayout, "height");
  const mobileWidth = _.get(mobileLayout, "width");
  const mobileHeight = _.get(mobileLayout, "height");
  const order = _.get(props, "order");
  const desktopBorder = _.get(props, "properties.desktop.border");
  const mobileBorder = _.get(props, "properties.mobile.border");
  const desktopShadow = _.get(props, "properties.desktop.shadow");
  const mobileShadow = _.get(props, "properties.mobile.shadow");
  const desktopAlt = _.get(props, "properties.desktop.alt");
  const mobileAlt = _.get(props, "properties.mobile.alt");

  const handleSelectFromLibrary = (medias) => {
    setView(null);

    const media = medias[0];
    if (!media || media.type !== "image") return;

    actionGroup.start();

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: IMAGE,
            name: "Image",
            order: order,
            properties: {
              desktop: {
                visible: true,
                layout: {
                  transform: { translate: desktopTransform, rotate: "0deg" },
                  width: desktopWidth,
                  height: desktopHeight,
                },
                crop: null,
                alt: desktopAlt || null,
                animation: null,
                border: desktopBorder || null,
                position: null,
                shadow: desktopShadow || null,
              },
              mobile: {
                visible: true,
                layout: {
                  transform: { translate: mobileTransform, rotate: "0deg" },
                  width: mobileWidth,
                  height: mobileHeight,
                },
                crop: null,
                alt: mobileAlt || desktopAlt || null,
                animation: null,
                border: mobileBorder || desktopBorder || null,
                position: null,
                shadow: mobileShadow || desktopShadow || null,
              },
            },
            content: media,
          },
        ],
        parent
      )
    );

    dispatch(deleteWidgets(editorSession, [id]));
    actionGroup.end();
  };

  return (
    <>
      <WidgetActions.Action
        className={styles.textButton}
        onClick={() => setView("library")}
      >
        Choose Image
      </WidgetActions.Action>

      <MediaLibraryModal
        show={view === "library"}
        storyboard={storyboardContext}
        mediaTypes={["image"]}
        onSelect={handleSelectFromLibrary}
        onClose={() => setView(null)}
        editorSession={editorSession}
      />
    </>
  );
};

const aiImageWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

export const AiImageWidget = React.memo(
  AiImageWidgetComponent,
  aiImageWidgetEquals
);

AiImageWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: ["e", "w", "s"],
  };
};

AiImageWidget.actions = (editor, breakpoint, props) => {
  const fetchFailed = _.get(props, "content.fetchFailed", false);

  if (breakpoint === "desktop" && fetchFailed) {
    return (
      <AiImageFetchFailedDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  return null;
};

AiImageWidget.processRequest = (widget) => {
  return widget;
};
